import React from "react"
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps, GetStaticPropsResult } from "next"
import { dev } from "../../lib/dev"
import { webUrl } from "../../config/web"
import { NextSeo } from "next-seo"
import { Layout } from "../../components/Layout/Layout"
import { css } from "@emotion/react"
import { SinglePage } from "../../components/SinglePage/SinglePage"
import { Section } from "../../components/Section"
import { CoursesList } from "../../components/CoursesList/CoursesList"
import { SubscribeForm } from "../../components/SubscribeForm/SubscribeForm"
import { Typography } from "antd"
import { AllProduct, getAllProducts, getAllTechnologies, ProductsColorsItem } from "../../lib/contentful/product/getAllProducts"
import { isArray } from "lodash"
import { getTechnologyBySlug } from "../../lib/contentful/product/getProductBySlug"
import { Entry, EntryCollection } from "contentful"
import { TypeProductEntitySkeleton, TypeTechnologySkeleton } from "../../lib/contentful/ContentfulVzdelanibudoucnostiTypes"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { documentToRenderOptions } from "../../components/Layout/documentToRenderOptions"

const TechnologySlug: React.FC<Props> = ({ slug, technology, contentfulProducts: { products, productsColors } }) => {
  if (!technology) {
    return <div>neni</div>
  }

  const {
    fields: { title, description, content, color }
  } = technology
  const url = `${webUrl}/technologie/${slug}`

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          url: url,
          title: title,
          description: description,
          images: [
            {
              url: `${webUrl}/images/header_laptop.jpg`,
              width: 1920,
              height: 1080,
              alt: "header_laptop"
            }
          ]
        }}
      />

      <Layout>
        <SinglePage
          color={color}
          title={title}
          css={css`
            background-color: #fff;
          `}>
          <Section
            css={css`
              padding: 30px 0 15px 0;
            `}>
            <article>{content ? documentToReactComponents(content, documentToRenderOptions) : null}</article>

            {products?.items?.length > 0 ? (
              <>
                <Typography.Title level={2}>Otevřené registrace kroužků technologie &quot;{title}&quot;</Typography.Title>
                <CoursesList
                  courses={products.items}
                  productsColors={productsColors}
                  informationOnly={true}
                  css={css`
                    margin: 20px 0 0 0;
                  `}
                />
              </>
            ) : null}
          </Section>

          <Section>
            <Typography.Title level={2}>Odběr novinek</Typography.Title>
            <SubscribeForm tags={["krouzky", "technologie", title]} />
          </Section>
        </SinglePage>
      </Layout>
    </>
  )
}

interface Props {
  slug: string
  technology: Entry<TypeTechnologySkeleton | null, undefined, string> | null
  contentfulProducts: AllProduct
}

export const getStaticPaths: GetStaticPaths = async (): Promise<GetStaticPathsResult> => {
  const contentfulTechnologies = await getAllTechnologies()
  const paths = contentfulTechnologies.items.map((d) => {
    return { params: { slug: d.fields.slug } }
  })

  return {
    paths,
    fallback: false
  }
}

export const getStaticProps: GetStaticProps = async ({ params }): Promise<GetStaticPropsResult<Props>> => {
  const slug: string = isArray(params?.slug) ? params?.slug?.[0] : params.slug

  const contentfulProducts = await getAllProducts({ "fields.subType[in]": ["course", "sampleCourse"] })
  const technology = await getTechnologyBySlug(slug)

  const filteredItems = (contentfulProducts?.products?.items ?? []).filter((p) => p.fields.technology.find((t) => t?.["fields"].slug === slug))
  const filteredContentfulProducts: AllProduct = contentfulProducts?.products?.items
    ? {
        productsColors: contentfulProducts.productsColors,
        products: {
          ...contentfulProducts.products,
          items: filteredItems,
          total: filteredItems.length
        }
      }
    : {
        products: [] as unknown as EntryCollection<TypeProductEntitySkeleton, undefined, string>,
        productsColors: [] as ProductsColorsItem[]
      }

  return {
    props: {
      slug,
      technology: technology?.entry || null,
      contentfulProducts: filteredContentfulProducts || {
        products: [] as unknown as EntryCollection<TypeProductEntitySkeleton, undefined, string>,
        productsColors: [] as ProductsColorsItem[]
      }
    },
    revalidate: dev ? 1 : 120
  }
}

export default TechnologySlug
